import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

type TemplateTooltipPosition = 'above' | 'below' | 'left' | 'right';

const positions: Record<TemplateTooltipPosition, ConnectedPosition> = {
  above: {
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'bottom',
    offsetY: -8,
  },
  below: {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: 8,
  },
  left: {
    originX: 'start',
    originY: 'center',
    overlayX: 'end',
    overlayY: 'center',
    offsetX: -8,
  },
  right: {
    originX: 'end',
    originY: 'center',
    overlayX: 'start',
    overlayY: 'center',
    offsetX: 8,
  },
};

const positionFallbacks: Record<TemplateTooltipPosition, TemplateTooltipPosition[]> = {
  above: ['above', 'below', 'right', 'left'],
  below: ['below', 'above', 'right', 'left'],
  left: ['left', 'right', 'above', 'below'],
  right: ['right', 'left', 'above', 'below'],
};

@Directive({
  selector: '[mojoTemplateTooltip]',
})
export class TemplateTooltipDirective implements OnDestroy {
  @Input() mojoTemplateTooltip: TemplateRef<unknown>;
  @Input() mojoTemplateTooltipPosition: TemplateTooltipPosition = 'above';
  @Input() mojoTemplateTooltipPanelClass: string;

  private overlayRef: OverlayRef;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
  ) {}

  @HostListener('mouseenter')
  @HostListener('focusin')
  @HostListener('click')
  open(): void {
    if (!this.mojoTemplateTooltip || this.overlayRef?.hasAttached()) return;

    if (!this.overlayRef) {
      const positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(this.elementRef)
        .withPositions(
          positionFallbacks[this.mojoTemplateTooltipPosition].map(
            (position) => positions[position],
          ),
        )
        .withFlexibleDimensions(false)
        .withPush(true)
        .withViewportMargin(8);

      this.overlayRef = this.overlay.create({
        panelClass: ['mojo-template-tooltip-panel', this.mojoTemplateTooltipPanelClass].filter(
          Boolean,
        ),
        positionStrategy,
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
      });
      this.overlayRef.outsidePointerEvents().subscribe(() => this.close());
    }

    this.overlayRef.attach(new TemplatePortal(this.mojoTemplateTooltip, this.viewContainerRef));
  }

  @HostListener('mouseleave')
  @HostListener('focusout')
  @HostListener('keydown.escape')
  close(): void {
    this.overlayRef?.detach();
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
  }
}
